import {
  useCombobox,
  UseComboboxGetInputPropsReturnValue,
  UseComboboxGetMenuPropsReturnValue,
} from 'downshift'
import { DropdownOptionModel } from 'ext-ui/components/Dropdown/components/DropdownOption/DropdownOption'
import { TextInputRef } from 'ext-ui/components/TextInput/TextInput'
import { LoadingState } from 'ext-ui/hooks/useLoadingStateAnimation'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react'
import { useDebounced } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { InfiniteScrollProps } from 'src/types'
import {
  BackendErrorPayload,
  useNormalizeBackendError,
} from 'src/utils/error.utils'

import {
  dropdownReducer,
  DropdownValue,
  initialDropdownState,
} from './DropdownState.reducer'

export type DropdownOptionGroup<T = unknown> = {
  id: string
  heading?: React.ReactNode
  options: DropdownOptionModel<T>[]
}

export type { DropdownValue }

export type DropdownOptionRow<T> =
  | { id: string; kind: 'option'; option: DropdownOptionModel<T> }
  | { id: string; kind: 'heading'; groupId: string; heading: React.ReactNode }
  | { id: 'loading'; kind: 'loading' }
  | { id: 'empty'; kind: 'empty'; hasSearchQuery: boolean }

export type FreeTextOptions<T> = {
  enabled: boolean
  freeTextOptionDetails?: T
  existingOptionHeader?: string
  freeTextOptionHeader?: string
}

/**
 * helper responsible for filtering options based on search input
 * abstracts away the logic of handling infinite scroll
 */
function filterGroups<T>(
  groups: DropdownOptionGroup<T>[],
  search: string,
  hasInfiniteScrollProps: boolean,
): DropdownOptionGroup<T>[] {
  const q = search.trim().toLowerCase()
  const filter = (opts: DropdownOptionModel<T>[]) => {
    if (!q || hasInfiniteScrollProps) return opts
    return opts.filter((o) => (o.label ?? o.value).toLowerCase().includes(q))
  }

  return groups
    .map((g) => ({
      ...g,
      options: filter(g.options).map((option) => ({
        ...option,
        selected: option.selected ?? false,
      })),
    }))
    .filter((g) => g.options.length > 0)
}

/**
 * deals with all the logic for handling free text input
 * and adds a "New Option" group if necessary
 */
function maybeInjectFreeText<T>(
  groups: DropdownOptionGroup<T>[],
  ctx: {
    search: string
    selectedValues: DropdownValue<T>[]
    freeText?: FreeTextOptions<T>
    originalGroups: DropdownOptionGroup<T>[]
    loading: boolean
  },
): DropdownOptionGroup<T>[] {
  const { freeText } = ctx
  if (!freeText?.enabled) return groups

  // don't show free text option while loading to prevent content jumping
  if (ctx.loading) return groups

  const search = ctx.search.trim()
  const selectedValues = ctx.selectedValues

  // Get all existing options from original groups
  const allExistingOptions = ctx.originalGroups.flatMap((g) => g.options)

  // Find selected values that are free text (don't exist in original groups)
  const selectedFreeTextValues = selectedValues.filter(
    (selected) =>
      !allExistingOptions.some((option) => option.value === selected.value),
  )

  // check if search exactly matches any existing option
  const searchExistsExact =
    search &&
    allExistingOptions.some(
      (option) =>
        (option.label ?? option.value).toLowerCase() === search.toLowerCase(),
    )

  // Show free text group if we have free text selections or valid search
  const shouldShowFreeText =
    selectedFreeTextValues.length > 0 || (search && !searchExistsExact)

  if (!shouldShowFreeText) return groups

  // Create free text options
  const freeTextOptions: DropdownOptionModel<T>[] = []

  // Add existing free text selections (always selected)
  selectedFreeTextValues.forEach((selected) => {
    freeTextOptions.push({
      value: selected.value,
      label: selected.label ?? selected.value,
      details: selected.details,
      selected: true,
    })
  })

  // Add current search term if it's valid and not already included
  if (search && !searchExistsExact) {
    const searchAlreadyIncluded = freeTextOptions.some(
      (opt) => opt.value === search || opt.label === search,
    )

    if (!searchAlreadyIncluded) {
      const isSearchSelected = selectedValues.some(
        (selected) => selected.value === search || selected.label === search,
      )

      freeTextOptions.push({
        value: search,
        label: search,
        details: freeText.freeTextOptionDetails,
        selected: isSearchSelected,
      })
    }
  }

  // If no free text options to show, return original groups
  if (freeTextOptions.length === 0) return groups

  const normalizedExisting = groups.map((g) => ({
    ...g,
    heading: g.heading ?? (freeText.existingOptionHeader || 'Existing Options'),
  }))

  const freeGroup: DropdownOptionGroup<T> = {
    id: 'free-text',
    heading: freeText.freeTextOptionHeader || 'New Option',
    options: freeTextOptions,
  }

  return [freeGroup, ...normalizedExisting]
}

/**
 * looks at the current state and builds the rows to be rendered in the dropdown
 */
function buildRows<T>(
  groups: DropdownOptionGroup<T>[],
  ctx: {
    pending: boolean
    infinite?: { loading: boolean }
    hasFreeText: boolean
    noneLabel: string | undefined
    search: string
  },
): DropdownOptionRow<T>[] {
  const rows: DropdownOptionRow<T>[] = []
  const hasOptions = groups.some((g) => g.options.length > 0)
  const loading = !!ctx.infinite?.loading || ctx.pending

  if (!hasOptions && !loading) {
    return [
      {
        id: 'empty',
        kind: 'empty',
        hasSearchQuery: !!ctx.search.trim(),
      },
    ]
  }

  if (ctx.noneLabel) {
    rows.push({
      id: 'select_none_option',
      kind: 'option',
      option: { value: '', label: ctx.noneLabel },
    })
  }

  if (!ctx.pending) {
    for (const g of groups) {
      const showHeading = groups.length > 1 || ctx.hasFreeText
      if (showHeading) {
        rows.push({
          id: `head:${g.id}`,
          kind: 'heading',
          groupId: g.id,
          heading: g.heading ?? g.id,
        })
      }
      for (const o of g.options) {
        rows.push({
          id: `opt:${g.id}:${o.value}`,
          kind: 'option',
          option: o,
        })
      }
    }
  }

  if (loading) {
    rows.push({
      id: 'loading',
      kind: 'loading',
    })
  }

  return rows
}

export interface UseDropdownLogicProps<T> {
  groups: DropdownOptionGroup<T>[]
  selectedValues: DropdownValue<T>[]
  /**
   * Label to show for the "none" option that clears the selection
   * @remarks `undefined` will hide the "none" option
   * @default translate('(none)')
   */
  noneLabel?: string
  infiniteScrollProps?: InfiniteScrollProps<string>
  isOpen?: boolean
  /**
   * Selection handler - supports both sync and async operations
   * Return Promise<void> for async operations with optimistic updates
   */
  onSelect: (selectedValue: DropdownValue<T>) => void | Promise<void>
  /**
   * Callback for when an async selection completes successfully
   * @remarks only called when onSelect returns a Promise
   * @param selectedValue the value that was selected
   */
  onSelectSuccess?: (selectedValue: DropdownValue<T> | undefined) => void
  /**
   * Callback for when an async selection fails
   * @remarks only called when onSelect returns a Promise
   * @param error
   */
  onSelectError?: (error: BackendErrorPayload<string>) => void
  /**
   * Callback for when an async selection completes, regardless of success or failure
   * @remarks only called when onSelect returns a Promise
   */
  onSelectSettled?: () => void
  onCancel?: () => void
  openOnMount?: boolean
  freeTextProps?: FreeTextOptions<T>
  searchable?: boolean
  /**
   * External loading state that manages async operations externally
   */
  loadingState?: LoadingState
  /** is multi select */
  multi?: boolean
}

export interface DropdownHandleProps {
  ref: React.RefObject<TextInputRef>
  onClick: (e: React.MouseEvent) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  toggleButtonProps: React.ButtonHTMLAttributes<HTMLButtonElement>
}

export interface UseDropdownLogicReturn<T> {
  rows: DropdownOptionRow<T>[]
  open: boolean
  getToggleButtonProps: ReturnType<typeof useCombobox>['getToggleButtonProps']
  getMenuProps: () => UseComboboxGetMenuPropsReturnValue & {
    anchorEl: HTMLElement | null
    width: number | undefined
  }
  getItemProps: ReturnType<typeof useCombobox>['getItemProps']
  getDropdownHandleProps: () => DropdownHandleProps
  getSearchInputProps: () => UseComboboxGetInputPropsReturnValue
  openMenu: ReturnType<typeof useCombobox>['openMenu']
  containerRef: React.RefObject<HTMLDivElement>
  optimisticSelectedValues: DropdownValue<T>[]
  asyncState: LoadingState
  asyncError: BackendErrorPayload<string> | undefined
  clearError: () => void
  /**
   * Selection handler enhanced - abstracts away loading for async flows and success feedback
   * should be used in all places whenever this hook is consumed
   */
  handleSelect: (selectedValue: DropdownValue<T>) => void | Promise<void>
  clearSearch: () => void
}

export const useDropdownLogic = <T>(
  props: UseDropdownLogicProps<T>,
): UseDropdownLogicReturn<T> => {
  const translate = useTranslate()
  const normalizeError = useNormalizeBackendError()

  const {
    groups,
    selectedValues,
    noneLabel = translate('(none)'),
    infiniteScrollProps,
    isOpen: controlledIsOpen,
    onSelect,
    onSelectSuccess,
    onSelectError,
    onSelectSettled,
    onCancel,
    openOnMount,
    freeTextProps,
    searchable,
    loadingState: externalLoadingState,
    multi = false,
  } = props

  const [state, dispatch] = useReducer(
    dropdownReducer<T>,
    initialDropdownState<T>(),
  )

  const searchInputRef = useRef<HTMLInputElement>(null)
  const handleRef = useRef<TextInputRef>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' })
  }, [])

  const containsValue = (
    arr: DropdownValue<T>[],
    v: DropdownValue<T>,
  ): boolean => {
    const vv = v.value
    return arr.some((x) => x.value === vv)
  }

  const handleSelect = useCallback(
    async (selectedValue: DropdownValue<T>) => {
      const current =
        state.asyncState === 'pending' && state.optimisticValues.length > 0
          ? state.optimisticValues
          : selectedValues

      const isDeselection = containsValue(current, selectedValue)

      const result = onSelect({ ...selectedValue, selected: !isDeselection })

      // If external loading state is managed, let parent handle async
      if (externalLoadingState !== undefined) {
        return
      }
      // Check if onSelect returned a Promise for internal async handling
      if (result && typeof result.then === 'function') {
        const operationId = Date.now().toString()
        dispatch({
          type: 'SELECT_ASYNC',
          payload: { selectedValue, operationId },
        })

        try {
          await result
          onSelectSuccess?.(selectedValue)
          dispatch({ type: 'ASYNC_COMPLETED', payload: { operationId } })
        } catch (error) {
          const normalizedError = normalizeError(error)
          onSelectError?.(normalizedError)
          dispatch({
            type: 'ASYNC_FAILED',
            payload: { operationId, error: normalizedError },
          })
        } finally {
          onSelectSettled?.()
        }
      }
    },
    [onSelect, normalizeError, externalLoadingState],
  )

  const filteredGroups = useMemo(
    () =>
      filterGroups<T>(
        groups,
        searchable ? state.search : '',
        !!infiniteScrollProps,
      ),
    [groups, state.search, searchable, infiniteScrollProps],
  )

  const withFreeText = useMemo(
    () =>
      maybeInjectFreeText<T>(filteredGroups, {
        search: searchable ? state.search : '',
        selectedValues,
        freeText: freeTextProps,
        originalGroups: groups,
        loading:
          infiniteScrollProps?.loading ??
          (state.asyncState === 'pending' || state.searchPending),
      }),
    [
      filteredGroups,
      state.search,
      searchable,
      selectedValues,
      freeTextProps,
      groups,
      infiniteScrollProps?.loading,
      state.asyncState,
      state.searchPending,
    ],
  )

  const hasFreeText = withFreeText.some((g) => g.id === 'free-text')

  const rows = useMemo(
    () =>
      buildRows<T>(withFreeText, {
        pending: state.searchPending,
        infinite: infiniteScrollProps,
        hasFreeText,
        noneLabel,
        search: searchable ? state.search : '',
      }),
    [
      withFreeText,
      noneLabel,
      state.searchPending,
      state.asyncState,
      infiniteScrollProps,
      hasFreeText,
      translate,
      state.search,
      searchable,
    ],
  )

  const wrappedFetch = useCallback(async (input: string, page: number) => {
    try {
      await Promise.resolve(infiniteScrollProps?.fetch?.(input, page));
    } finally {
      dispatch({ type: 'SEARCH_PENDING_COMPLETED' });
    }
  }, [infiniteScrollProps?.fetch]);

  const { debouncedF: debouncedFetch } = useDebounced(wrappedFetch, 400);

  // cleanup search pending whenever loading state changes to false
  useEffect(() => {
    if (!infiniteScrollProps?.loading) {
      dispatch({ type: 'SEARCH_PENDING_COMPLETED' })
    }
  }, [infiniteScrollProps])

  const {
    getToggleButtonProps,
    getMenuProps: _getMenuProps,
    getItemProps,
    getInputProps,
    isOpen: open,
    openMenu,
    setHighlightedIndex,
  } = useCombobox<DropdownOptionRow<T>>({
    items: rows,
    inputValue: state.search,
    isOpen: controlledIsOpen,
    itemToString: (row) =>
      row?.kind === 'option' ? (row.option.label ?? row.option.value) : '',
    isItemDisabled: (row) => row.kind !== 'option' || !!row.option.disabled,
    onIsOpenChange: ({ isOpen, type }) => {
      if (isOpen) {
        dispatch({ type: 'USER_ACTION', action: 'open' })
        dispatch({ type: 'REQUEST_FOCUS', target: 'search' })
        if (!searchable) {
          // Highlight first option when not searchable
          setHighlightedIndex(0)
        }
      } else {
        // check Downshift.js action type to avoid firing onCancel with our custom handlers
        if (type !== '__item_click__' && type !== '__input_keydown_enter__') {
          onCancel?.()
        }
        // Only refocus the handle when the user explicitly closed via toggle or Escape.
        if (
          type === useCombobox.stateChangeTypes.ToggleButtonClick ||
          type === useCombobox.stateChangeTypes.InputKeyDownEscape
        ) {
          // Defer to next tick so Downshift can finish closing before we move focus.
          queueMicrotask(() => dispatch({ type: 'REQUEST_FOCUS', target: 'handle' }));
        }
        // For blur / outside click, do NOT force focus → allows natural click-away close.
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem?.kind !== 'option') return
      if (!multi) {
        dispatch({ type: 'SEARCH_CHANGED', payload: '' })
      }
      handleSelect(selectedItem.option)
    },
    onInputValueChange: ({ inputValue }) => {
      dispatch({ type: 'SEARCH_CHANGED', payload: inputValue ?? '' })
      if (infiniteScrollProps && state.initialized) {
        dispatch({ type: 'SEARCH_PENDING_STARTED' })
        debouncedFetch(inputValue ?? '', 0)
      }
    },
    stateReducer: (downshiftState, downshiftAction) => {
      const t = downshiftAction.type
      const changes = downshiftAction.changes;

      // handle Enter key for free text selection
      if (t === useCombobox.stateChangeTypes.InputKeyDownEnter) {
        if (freeTextProps?.enabled && state.search.trim()) {
          const exactMatch = groups.some((group) =>
            group.options.some(
              (option) =>
                (option.label ?? option.value).toLowerCase() ===
                state.search.toLowerCase(),
            ),
          )

          // no exact match, and input is the focus, select the free text option
          if (!exactMatch && downshiftState.highlightedIndex === -1) {
            dispatch({ type: 'SEARCH_CHANGED', payload: '' })
            handleSelect({
              value: state.search.trim(),
              details: freeTextProps.freeTextOptionDetails,
            })

            return {
              ...changes,
              inputValue: downshiftState.inputValue,
              isOpen: false,
            }
          }
        }
        return {
          ...changes,
          inputValue: downshiftState.inputValue,
          isOpen: multi ? true : changes.isOpen,
        }
      }

      if (t === useCombobox.stateChangeTypes.ItemClick) {
        return {
          ...changes,
          isOpen: multi ? true : changes.isOpen,
          inputValue: downshiftState.inputValue,
        }
      }

      if (t === useCombobox.stateChangeTypes.InputBlur) {
        return {
          ...changes,
          inputValue: downshiftState.inputValue,
        };
      }

      return downshiftAction.changes
    },
  })

  const onHandleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open && (e.key === 'Enter' || e.key.length === 1)) {
        openMenu()
        e.preventDefault()
        if (
          e.key.length === 1 &&
          !(e.metaKey || e.ctrlKey) &&
          e.code !== 'Space'
        ) {
          dispatch({ type: 'SEARCH_CHANGED', payload: e.key })
        }
        return
      } else if (!open && (e.key === 'Backspace' || e.key === 'Delete')) {
        handleSelect({ value: '', label: noneLabel })
        e.preventDefault()
      }
    },
    [open, openMenu, noneLabel, handleSelect],
  )

  const getDropdownHandleProps = useCallback(
    (): DropdownHandleProps => ({
      ref: handleRef,
      onClick: () => openMenu(),
      onKeyDown: onHandleKeyDown,
      toggleButtonProps: getToggleButtonProps(),
    }),
    [openMenu, onHandleKeyDown, getToggleButtonProps],
  )

  const getMenuProps = useCallback(
    () => ({
      ..._getMenuProps({}, { suppressRefError: true }),
      anchorEl: handleRef.current?.container ?? null,
      width: containerRef.current?.clientWidth,
    }),
    [_getMenuProps],
  )

  const getSearchInputProps = useCallback(
    () => getInputProps({ ref: searchInputRef }, { suppressRefError: true }),
    [getInputProps],
  )

  useLayoutEffect(() => {
    if (openOnMount) {
      openMenu()
    }
  }, [openMenu, openOnMount])

  useEffect(() => {
    if (open && !state.initialized) {
      // set initial search state for free text
      if (freeTextProps?.enabled && selectedValues.length === 1) {
        const singleValue = selectedValues[0]
        const selectedExists = groups.some((g) =>
          g.options.some((o) => o.value === singleValue.value),
        )

        if (!selectedExists) {
          const freeTextValue = singleValue.label ?? singleValue.value
          dispatch({ type: 'SEARCH_CHANGED', payload: freeTextValue })
          if (infiniteScrollProps) {
            debouncedFetch(freeTextValue, 0)
          }
        }
      }

      dispatch({ type: 'INITIALIZED' })
    }
  }, [
    open,
    state.initialized,
    freeTextProps?.enabled,
    selectedValues,
    groups,
    infiniteScrollProps,
    debouncedFetch,
  ])

  // Unified focus management - handles all focus requests from state machine
  useLayoutEffect(() => {
    if (state.focusRequest === 'handle') {
      handleRef.current?.focus()
      dispatch({ type: 'FOCUS_HANDLED' })
    } else if (state.focusRequest === 'search') {
      searchInputRef.current?.focus({ preventScroll: true })
      dispatch({ type: 'FOCUS_HANDLED' })
    }
  }, [state.focusRequest])

  // Effect to sync external loading state with internal reducer state
  const prevExternalLoadingState = useRef<LoadingState | undefined>(undefined)

  useEffect(() => {
    if (externalLoadingState === undefined) return

    const prev = prevExternalLoadingState.current
    prevExternalLoadingState.current = externalLoadingState

    // Only dispatch if state actually changed
    if (prev === externalLoadingState) return

    switch (externalLoadingState) {
      case 'pending':
        // Only trigger if not already in loading state
        if (state.asyncState !== 'pending') {
          if (state.optimisticValues.length === 1) {
            dispatch({
              type: 'SELECT_ASYNC',
              payload: {
                selectedValue: state.optimisticValues[0],
                operationId: 'external',
              },
            })
          }
        }
        break
      case 'success':
        dispatch({
          type: 'ASYNC_COMPLETED',
          payload: { operationId: 'external' },
        })
        break
      case 'error':
        dispatch({ type: 'ASYNC_FAILED', payload: { operationId: 'external' } })
        break
      case 'idle':
        // Reset to idle if coming from loading/success/error
        if (state.asyncState !== 'idle') {
          dispatch({ type: 'CLEAR_ERROR' })
        }
        break
    }
  }, [externalLoadingState, state.asyncState, state.optimisticValues])

  // Create optimistic selected value - use optimistic during loading, otherwise use selectedValue
  const optimisticSelectedValues = useMemo(() => {
    if (state.asyncState === 'pending' && state.optimisticValues.length > 0) {
      return state.optimisticValues
    }
    return selectedValues
  }, [state.asyncState, state.optimisticValues, selectedValues])

  const clearSearch = useCallback(() => {
    dispatch({ type: 'SEARCH_CHANGED', payload: '' });
  }, []);

  return {
    rows,
    open,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    getDropdownHandleProps,
    getSearchInputProps,
    openMenu,
    containerRef,
    optimisticSelectedValues,
    asyncState: state.asyncState,
    asyncError: state.asyncError,
    clearError,
    handleSelect,
    clearSearch
  }
}
